import { Injectable, Injector, inject, provideAppInitializer } from '@angular/core';
import { Observable, catchError, filter, from, of, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppTheme } from '@core/shared/domain';
import { ThemeHandlerService, redirectToErrorPage } from '@core/shared/util';
import { AuthConfig, ConfigLoaderFacade, ConfigLoaderService } from '@mp/shared/data-access';
import { AuthService } from '@mpauth/shared/data-access';

import { NavigationRouteBuilderService } from '../navigation/navigation-route-builder.service';

import { ProfileVerifyService } from './profile-verify.service';

interface InitialRouteState {
  url: string;
  title: string;
}

@Injectable()
export class AppInitService {
  private readonly injector = inject(Injector);
  private readonly configLoaderFacade = inject(ConfigLoaderFacade);


  init(): Observable<unknown> {
    return this.configLoaderFacade.authConfig$.pipe(
      switchMap((authConfig) => this.setupAuth(authConfig)),
      switchMap((routerState) => this.setupAppTheme().pipe(map(() => routerState))),
      switchMap((routerState) => this.initializeApp(routerState)),
      catchError(() => {
        redirectToErrorPage();
        throw Error('Error during app initialization, navigating to error page');
      }),
    );
  }

  private setupAuth(authConfig: AuthConfig): Observable<InitialRouteState | undefined> {
    const authService = this.injector.get(AuthService);

    const state: InitialRouteState = {
      url: self.location.href,
      title: document.title,
    };

    return from(authService.setupAuthAndLogin(authConfig, state));
  }

  private initializeApp(routerState: InitialRouteState | undefined): Observable<unknown> {
    const profileVerifyService = this.injector.get(ProfileVerifyService);
    const routeBuilderService = this.injector.get(NavigationRouteBuilderService);

    return of(routerState).pipe(
      switchMap(() => profileVerifyService.verifyProfileConfig()),
      filter(Boolean),
      // Try to init routes only if profile verification was successful - only then we can successfully fetch app routes
      tap(() => routeBuilderService.initRoutes()),
      tap(() => this.restoreLastVisitedPageFromState(routerState)),
    );
  }

  private setupAppTheme(): Observable<unknown> {
    const themeHandlerFacade = this.injector.get(ThemeHandlerService);

    return this.configLoaderFacade.environmentConfig$.pipe(
      switchMap(({ theme }) => themeHandlerFacade.setTheme(theme as AppTheme)),
    );
  }

  private restoreLastVisitedPageFromState(state: InitialRouteState | undefined): void {
    if (state?.url && state.url !== self.location.href) {
      /**
       * Die URL nach dem Redirect zum IdentityServer wiederherstellen, sodass uns der Router zur ursprünglich gewünschten Seite bringt.
       * Das geschieht hier ohne neuen Eintrag in der History und ohne den Reload des Browsers zu triggern.
       * Direkt den Router zu benutzen funktioniert nicht, da der AppInitializer hier vorm Router-Init läuft.
       */
      self.history.replaceState({}, state.title || document.title, state.url);
    }
  }
}

export const appInitProvider = [
  ConfigLoaderService,
  ConfigLoaderFacade,
  AppInitService,
  provideAppInitializer((appInitService: AppInitService = inject(AppInitService)) => appInitService.init()),
];
